import React, { useState, useEffect } from 'react';
import {
  Snackbar,
  Alert,
  Button,
  Box,
  Typography,
  IconButton,
  Slide
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Close as CloseIcon,
  CloudDownload as UpdateIcon
} from '@mui/icons-material';

const PWAUpdateNotification = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [registration, setRegistration] = useState(null);
  const [isInstalling, setIsInstalling] = useState(false);

  useEffect(() => {
    // Listen for service worker update events
    const handleUpdateAvailable = (event) => {
      console.log('Update available event received:', event.detail);
      setRegistration(event.detail.registration);
      setUpdateAvailable(true);
    };

    window.addEventListener('sw-update-available', handleUpdateAvailable);

    return () => {
      window.removeEventListener('sw-update-available', handleUpdateAvailable);
    };
  }, []);

  const handleUpdateApp = async () => {
    if (!registration || !registration.waiting) {
      return;
    }

    setIsInstalling(true);

    try {
      // Tell the service worker to skip waiting
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      
      // Wait for the new service worker to take control
      await new Promise((resolve) => {
        const handleControllerChange = () => {
          navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
          resolve();
        };
        navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
      });

      // Reload the page to get the new version
      window.location.reload();
    } catch (error) {
      console.error('Error updating app:', error);
      setIsInstalling(false);
    }
  };

  const handleDismiss = () => {
    setUpdateAvailable(false);
    // Store dismissal in session storage to prevent showing again in this session
    sessionStorage.setItem('update-dismissed', 'true');
  };

  if (!updateAvailable) {
    return null;
  }

  return (
    <Snackbar
      open={updateAvailable}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      TransitionComponent={Slide}
      TransitionProps={{ direction: 'down' }}
      sx={{ 
        zIndex: 9999,
        mt: 1
      }}
    >
      <Alert
        severity="info"
        variant="filled"
        sx={{
          minWidth: 350,
          alignItems: 'center',
          background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
          color: 'white',
          '& .MuiAlert-icon': {
            color: 'white'
          }
        }}
        icon={<UpdateIcon />}
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              color="inherit"
              size="small"
              onClick={handleUpdateApp}
              disabled={isInstalling}
              startIcon={<RefreshIcon />}
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&:disabled': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: 'rgba(255, 255, 255, 0.6)'
                }
              }}
            >
              {isInstalling ? 'Updating...' : 'Update'}
            </Button>
            <IconButton
              size="small"
              color="inherit"
              onClick={handleDismiss}
              disabled={isInstalling}
              sx={{ 
                color: 'white',
                opacity: 0.8,
                '&:hover': { opacity: 1 }
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        }
      >
        <Box>
          <Typography variant="body2" fontWeight="bold">
            New Version Available!
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            T-TRADES has been updated with new features and improvements.
          </Typography>
        </Box>
      </Alert>
    </Snackbar>
  );
};

export default PWAUpdateNotification; 